import { useMemo } from 'react';

import { aiExperienceApplicationMessages } from '@atlaskit/editor-common/ai-messages';
import { fg } from '@atlaskit/platform-feature-flags';
import type { Suggestion } from '@atlassian/generative-ai-modal/screens/UserInputCommandPalette';

import {
	TELL_AI,
	TELL_AGENT,
	USE_GENERAL_AI_KNOWLEDGE,
	USE_ROVO_ORGANISATION_KNOWLEDGE,
} from '../utils/constants';

import { useAIExperienceCommonDataContext } from './useAIExperienceCommonData';

/**
 * The use page knowledge toggle should only be visible when:
 * - there are agent conversation starters
 * - there is user input or ADF input but no general AI suggestions
 */
export const usePageKnowledgeToggle = ({
	hasInput,
	suggestions,
	agentVisible,
	nestedMenuSelected,
}: {
	agentVisible: boolean;
	hasInput: boolean;
	nestedMenuSelected: boolean;
	suggestions: Suggestion[];
}): {
	isEnabled: boolean;
	isVisible: boolean;
	onToggle: () => void;
} => {
	const { sendToAIExperienceMachine, includeKnowledgeFromCurrentPage } =
		useAIExperienceCommonDataContext();

	const pageKnowledgeIsVisible = useMemo(() => {
		/**
		 * Simplify the logic to not rely on the group heading copy and rely on constant values.
		 */
		if (fg('ai-smart-button-team-25')) {
			const isGeneralAISuggestionRemaining = [
				TELL_AI,
				TELL_AGENT,
				USE_GENERAL_AI_KNOWLEDGE,
				USE_ROVO_ORGANISATION_KNOWLEDGE,
			].includes(suggestions[0]?.value);
			return !!(agentVisible || (isGeneralAISuggestionRemaining && !nestedMenuSelected));
		}

		const hasGeneralAiSuggestions =
			suggestions[0]?.groupHeading ===
			aiExperienceApplicationMessages.atlassianIntelligenceGroupHeading.defaultMessage;

		return !!(agentVisible || (hasInput && !hasGeneralAiSuggestions && !nestedMenuSelected));
	}, [agentVisible, nestedMenuSelected, hasInput, suggestions]);

	const pageKnowledgeProps = useMemo(() => {
		return {
			isEnabled: !!includeKnowledgeFromCurrentPage,
			isVisible: pageKnowledgeIsVisible,
			onToggle: () => sendToAIExperienceMachine({ type: 'toggle use page knowledge' }),
		};
	}, [includeKnowledgeFromCurrentPage, pageKnowledgeIsVisible, sendToAIExperienceMachine]);

	return pageKnowledgeProps;
};
